import ENV from "app/utils/ENV";
import textHelpers from "app/utils/textHelpers";
import timeHelpers from "app/utils/timeHelpers";
import valueHelpers from "app/utils/valueHelpers";
import store from "app/redux/store";

let frequencyHelpers = {
  getDoneToday(values) {
    let doneToday = values
      .map((value) => {
        let ts = valueHelpers.getTimestamp({
          value,
          returnValue: true,
          inheritGrandchildren: false,
          inheritProgress: false,
        });
        let lastTs = ts.timestamp;
        if (timeHelpers.wasToday(lastTs)) {
          return {
            ...value,
            timestamp: lastTs,
          };
        }
      })
      .filter((x) => !!x);
    doneToday = textHelpers.newestFirst(doneToday, "timestamp");
    return doneToday;
  },
  // Items overdue by more than max(frequency*3, 90) days are "dormant" —
  // they surface in a separate collapsed section rather than cluttering Due.
  isDormant(value, dueIn) {
    if (dueIn >= 0) return false;
    let freq = value.frequency || 7;
    return dueIn < -Math.max(freq * 3, 90);
  },
  getDueValues(values = []) {
    let { preferences } = store.getState();
    let { dismissedTodos, todoIds } = preferences;
    let dueValues = values
      .map((value) => {
        let includeFromIds = todoIds.includes(value.id);
        if (!!value.frequency || !!value.snooze_until || includeFromIds) {
          if (valueHelpers.isDismissed(value)) {
            return null;
          }

          let dueIn = frequencyHelpers.getDueIn(value);
          if (dueIn !== null || includeFromIds) {
            let status = frequencyHelpers.getStatus(dueIn, value);
            // return one of: ['done', 'due', 'overdue', 'snoozed', 'upcoming']
            if (
              ["due", "overdue"].includes(status) ||
              (includeFromIds && !["done", "snoozed"].includes(status))
            ) {
              // Dormant items go to their own section, not Due
              if (dueIn !== null && frequencyHelpers.isDormant(value, dueIn)) {
                return null;
              }
              let dueAt = null;
              if (dueIn !== null) {
                dueAt = timeHelpers.daysFromNow(dueIn);
                dueAt = timeHelpers.beginningOfDay(dueAt).toISOString();
              }
              return {
                ...value,
                dueIn,
                dueAt,
              };
            }
          }
        }
      })
      .filter((x) => !!x);

    dueValues = textHelpers.newestFirst(dueValues, "dueAt");
    return dueValues;
  },
  getDormantValues(values = []) {
    let { preferences } = store.getState();
    let { dismissedTodos, todoIds } = preferences;
    let dormantValues = values
      .map((value) => {
        let includeFromIds = todoIds.includes(value.id);
        if (!!value.frequency || includeFromIds) {
          if (valueHelpers.isDismissed(value)) return null;
          let dueIn = frequencyHelpers.getDueIn(value);
          if (dueIn !== null && frequencyHelpers.isDormant(value, dueIn)) {
            let dueAt = timeHelpers.daysFromNow(dueIn);
            dueAt = timeHelpers.beginningOfDay(dueAt).toISOString();
            return { ...value, dueIn, dueAt };
          }
        }
      })
      .filter((x) => !!x);
    dormantValues = textHelpers.newestFirst(dormantValues, "dueAt");
    return dormantValues;
  },
  getUpcoming(values = [], range = 7) {
    let upcoming = values
      .map((value) => {
        let dueIn = frequencyHelpers.getDueIn(value);
        if (!!dueIn && dueIn > 0 && dueIn < range) {
          return {
            ...value,
            dueIn,
          };
        }
      })
      .filter((x) => !!x);
    upcoming = textHelpers.oldestFirst(upcoming, "dueIn");
    return upcoming;
  },
  getDismissedIds() {
    let { preferences } = store.getState();
    let { dismissedTodos } = preferences;
    let dismissedIds = dismissedTodos
      .map((x) => {
        let id = parseInt(x.split(`:`)[0]);
        let dismissedAt = x.slice(x.indexOf(":") + 1);
        if (!!dismissedAt && timeHelpers.wasToday(dismissedAt)) {
          return id;
        }
      })
      .filter((x) => !!x);
    return dismissedIds;
  },
  getStatus(dueIn, value) {
    // return one of: ['done', 'due', 'overdue', 'snoozed', 'upcoming']
    let status = null;
    if (dueIn == 0) {
      let lastTs = valueHelpers.getTimestamp({
        value,
        inheritGrandchildren: true,
        acceptCreatedAt: false,
      });
      if (timeHelpers.wasToday(lastTs)) {
        status = "done";
      } else {
        status = "due";
      }
    } else if (dueIn < 0) {
      status = "overdue";
    } else if (dueIn > 0) {
      if (!!value.snooze_until && valueHelpers.isSnoozed(value)) {
        status = "snoozed";
      } else {
        status = "upcoming";
      }
    }
    return status;
  },
  getDueString(dueIn, status, value) {
    let string = null;
    let plural = typeof dueIn != "number" || Math.abs(dueIn) == 1 ? "" : "s";
    if (!status) {
      string = null;
    } else if (status == "done") {
      let ts = valueHelpers.getTimestamp({ value, returnValue: true });
      string = `${textHelpers.formatTime(ts.timestamp)}`;
      if (ts.value.id == value.id) {
        string = "✓ " + string;
      } else {
        string += ` via "${ts.value.text}"`;
      }
      // string = `Done today`
    } else if (status == "due") {
      string = `Due today`;
    } else if (status == "overdue") {
      if (dueIn == -1) {
        string = "Due yesterday";
      } else {
        string = `Due ${-dueIn} day${plural} ago`;
      }
    } else if (["upcoming", "snoozed"].includes(status)) {
      if (dueIn == 1) {
        string = "Due tomorrow";
      } else {
        string = `Due in ${dueIn} day${plural}`;
      }
    }
    // else if(status == 'snoozed'){}
    return string;
  },
  getDueTimestamp(dueIn = null) {
    if (dueIn == null) {
      return null;
    }
    let today = new Date().getTime();
    let due = today + timeHelpers.msInDay * dueIn;
    let dueTs = new Date(due);
    return dueTs.toISOString();
  },
  getFreshness(value, timestamp = null, now = null) {
    let frequency = value.frequency || ENV.CYCLE;
    if (!timestamp) {
      let ts = valueHelpers.getTimestamp({
        value,
        returnValue: true,
        inheritGrandchildren: null,
        acceptCreatedAt: false,
      });
      timestamp = ts.timestamp;
    }
    // let progressions = valueHelpers.getProgressions({value})
    let daysAgo = timeHelpers.daysAgo(timestamp, now);
    let expiresIn = Math.max(0, frequency - daysAgo);
    let freshness = expiresIn / frequency;
    // console.log(value.text, daysAgo, frequency, freshness);
    if (frequency == 1) {
      // use hours rather than days
      let hoursAgo = timeHelpers.hoursAgo(timestamp, now);
      expiresIn = Math.max(0, 24 - hoursAgo);
      freshness = expiresIn / 24;
    }
    // freshness = Math.max(0.001, freshness);
    return freshness;
  },
  getStaleness(value, timestamp = null, now = null) {
    let frequency = value.frequency || ENV.CYCLE;
    if (!timestamp) {
      let ts = valueHelpers.getTimestamp({
        value,
        returnValue: true,
        inheritGrandchildren: null,
        acceptCreatedAt: false,
      });
      timestamp = ts.timestamp;
    }
    let daysAgo = timeHelpers.daysAgo(timestamp, now);

    // if (daysAgo < frequency) return 0;
    if (daysAgo < frequency) return 0;

    // let staleBy = daysAgo + 1 - frequency;
    let staleBy = daysAgo - frequency;

    let staleness = Math.min(1, staleBy / frequency);
    return staleness;
  },
  getDueIn(value) {
    // to have a dueIn value, the item must have either a frquency or a snooze_until
    let dueIn = null;

    if (!!value.completed) return dueIn;

    let inheritGrandchildren = null;
    let acceptCreatedAt = true;
    // acceptCreatedAt = false;
    // we may want to accept created_at for completed child nodes, but not for open items

    // accepting for now, for newly created revisions

    let ts = valueHelpers.getTimestamp({
      value,
      returnValue: true,
      inheritGrandchildren,
      acceptCreatedAt,
    });
    let daysAgo = timeHelpers.daysAgo(ts.timestamp);

    if (!ts.timestamp) {
      dueIn = 0;
    } else if (daysAgo == 0) {
      dueIn = 0;
    } else {
      if (!!value.frequency) {
        dueIn = value.frequency - daysAgo;
      }
    }

    if (!!value.snooze_until && !value.completed) {
      let unsnoozeIn = -timeHelpers.daysAgo(value.snooze_until);
      if (unsnoozeIn >= 0) {
        // if(!dueIn || unsnoozeIn < dueIn){
        // if(unsnoozeIn == 0){
        //   return "done"
        // }
        // snooze is upcoming
        dueIn = unsnoozeIn;
        // }
      } else if (unsnoozeIn < 0) {
        let unsnoozedDaysAgo = -unsnoozeIn;
        if (unsnoozedDaysAgo < daysAgo) {
          // overdue snooze
          // console.log(value.text, dueIn, daysAgo, ts, unsnoozedDaysAgo)
          dueIn = unsnoozeIn;
        }
      }
    }

    return dueIn;
  },
};

export default frequencyHelpers;
